import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import { commonApi, excApi as detailsApi, handleApiCall, manageApi } from '@exchange-frontends/api';
import { AppCrashComponent, ErrorBoundary, GlobalSkeletonStyles } from '@exchange-frontends/components';
import { useIMS } from '@exchange-frontends/custom-hooks';
import { makeVersionString } from '@exchange-frontends/utils';

import { PREVIEW_ROUTES } from '../../../constants';
import { FULL_INIT_STATE, useStore } from '../store';
import NotFoundPage from './NotFoundPage';
import AppDetails from './details/AppDetails';
import CCInstallationInstructions from './details/CCInstallationInstructions';
import PreviewAppDetails from './details/PreviewAppDetails';

export const ConfigContext = React.createContext(null);
export const UserDataContext = React.createContext({ accessToken: '', profile: {} });

window.xchgVersions = {
  ...(window.xchgVersions ?? {}),
  details: makeVersionString(),
};

/**
 * App component including the router and react-spectrum providers
 * @component
 * @category Experience Cloud Details
 */
const App = ({ config }) => {
  detailsApi.settings.setEndpoint(config?.apiUrl ?? process.env.ENDPOINT);
  detailsApi.settings.setApiKey(config?.apiKey ?? process.env.API_KEY);
  detailsApi.reviewSettings.setApiKey(config?.rnrApiKey ?? process.env.REACT_APP_CLIENT_ID);
  commonApi.staticDataSettings.setEndpoint(config?.staticDataEndpoint ?? process.env.STATIC_DATA_ENDPOINT);
  commonApi.ccesSettings.setEndpoint(config?.ccesEndpoint ?? process.env.CCES_ENDPOINT);
  commonApi.ccesSettings.setApiKey(config?.ccesApiKey ?? process.env.CCES_API_KEY);
  commonApi.languagesSettings.setEndpoint(config?.languagesListEndpoint ?? process.env.LANGUAGES_LIST_ENDPOINT);
  commonApi.getCreativeCloudLinkSettings.setEndpoint(config?.apiUrl ?? process.env.ENDPOINT);
  commonApi.getCreativeCloudLinkSettings.setApiKey(config?.apiKey ?? process.env.API_KEY);
  commonApi.ccMostPopularTagsSettings.setEndpoint(
    config?.ccMostPopularTagsEndpoint ?? process.env.CC_MOST_POPULAR_TAGS_ENDPOINT
  );
  detailsApi.arsEndpointSettings.setEndpoint(config?.appRegistryUrl ?? process.env.APP_REGISTRY_URL);
  detailsApi.arsEndpointSettings.setApiKey(config?.ccesApiKey ?? process.env.CCES_API_KEY);
  detailsApi.rnrHeadEndpointSettings.setEndpoint(config?.rnrEndpoint ?? process.env.RNR_ENDPOINT);
  detailsApi.rnrHeadEndpointSettings.setApiKey(config?.rnrApiKey ?? process.env.REACT_APP_CLIENT_ID);
  manageApi.appRegistrySetup.setEndpoint(config?.appRegistryUrl ?? process.env.APP_REGISTRY_URL);
  manageApi.appRegistrySetup.setApiKey(config?.apiKey ?? process.env.API_KEY);

  const [encounteredError, setEncounteredError] = useState(false);
  const [imsProfile, setImsProfile] = useState(null);
  const ims = useIMS();
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);

  useEffect(() => {
    let mounted = true;
    const handler = handleApiCall(detailsApi.getRnrEndpoints);
    (async () => {
      try {
        const res = await handler.run();
        if (mounted) {
          detailsApi.reviewSettings.setEndpoint(res);
          useStore.setState({ rnrEndpointsLoading: false });
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          useStore.setState({ shouldShowRnr: false, rnrEndpointsLoading: false });
        }
      }
    })();
    return () => {
      mounted = false;
      handler.controller.abort();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!ims?.isMock && ims?.initialized && ims?.isSignedInUser()) {
      ims.getProfile().then(async (profile) => {
        if (mounted) {
          setImsProfile(profile);
        }
      });
    }
    return () => {
      mounted = false;
    };
  }, [ims]);

  useEffect(() => {
    return () => {
      useStore.setState(FULL_INIT_STATE);
    };
  }, []);

  return (
    <>
      <GlobalSkeletonStyles />
      <ErrorBoundary isError={encounteredError} errorComponent={<AppCrashComponent appName="Details" />}>
        {() => (
          <>
            <ConfigContext.Provider value={config}>
              <UserDataContext.Provider
                value={{
                  profile: imsProfile,
                  accessToken,
                }}
              >
                <Routes>
                  <Route
                    path="/cc/installation-instructions/:versionId/:name"
                    element={<CCInstallationInstructions />}
                  />
                  <Route
                    path="/cc/:id/*"
                    element={<AppDetails setEncounteredError={setEncounteredError} cloud="cc" />}
                  />
                  <Route
                    path="/ec/:id/*"
                    element={<AppDetails setEncounteredError={setEncounteredError} cloud="ec" />}
                  />
                  <Route
                    path="/dc/:id/*"
                    element={<AppDetails setEncounteredError={setEncounteredError} cloud="dc" />}
                  />
                  <Route
                    path="/integrations/:id/*"
                    element={
                      <AppDetails config={config} setEncounteredError={setEncounteredError} cloud="cc" isIntegration />
                    }
                  />
                  {Object.entries(PREVIEW_ROUTES).flatMap(([cloud, paths]) =>
                    paths.map((path) => (
                      <Route
                        key={path}
                        path={path}
                        element={<PreviewAppDetails setEncounteredError={setEncounteredError} cloud={cloud} />}
                      />
                    ))
                  )}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </UserDataContext.Provider>
            </ConfigContext.Provider>
          </>
        )}
      </ErrorBoundary>
    </>
  );
};

export default App;
